import { useState, useEffect } from "react";
import { Plus, X, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "ISO 9001", "ISO 14001", "ISO 22000", "ISO 27001", "ISO 45001", 
  "ISO 31000", "ISO 22301", "ISO 28000", "ISO 50001", "ISO 42001",
  "ISO 37001", "HACCP", "FSSC 22000", "GMP", "Food Safety", 
  "Risk Management", "Business Continuity", "Other"
];

const CERTIFICATION_LEVELS = [
  "Foundation", "Lead Implementer", "Lead Auditor", "Internal Auditor", 
  "Transition", "Manager", "Senior Lead Implementer", "Senior Lead Auditor"
];

const DEFAULT_DELIVERY_FORMATS = [
  { format: "self-study", price: 375, duration: "16 hours" },
  { format: "online-live", price: 475, duration: "2 days" },
  { format: "e-learning", price: 400, duration: "Self-paced" }
];

const emptyCoursee = {
  title: "",
  category: "",
  certification_level: "Foundation",
  description: "",
  key_topics: [""],
  prerequisites: "",
  learning_outcomes: [""],
  delivery_formats: [...DEFAULT_DELIVERY_FORMATS],
  brochure_url: "",
  image_url: "",
  is_featured: false,
  is_active: true
};

const CourseEditor = ({ open, onOpenChange, course, onSave, getAuthHeader }) => {
  const [formData, setFormData] = useState(emptyCoursee);
  const [saving, setSaving] = useState(false);
  const isEditing = !!course?.id;

  useEffect(() => {
    if (course) {
      setFormData({
        ...emptyCoursee,
        ...course,
        key_topics: course.key_topics?.length ? course.key_topics : [""],
        learning_outcomes: course.learning_outcomes?.length ? course.learning_outcomes : [""],
        delivery_formats: course.delivery_formats?.length ? course.delivery_formats : [...DEFAULT_DELIVERY_FORMATS]
      });
    } else {
      setFormData(emptyCoursee);
    }
  }, [course, open]);

  const handleArrayFieldChange = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1) {
      setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
    }
  };

  const handleDeliveryFormatChange = (index, key, value) => {
    const updated = [...formData.delivery_formats];
    updated[index] = { ...updated[index], [key]: key === 'price' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, delivery_formats: updated });
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.category) {
      toast.error("Please fill in at least title and category");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        key_topics: formData.key_topics.filter(t => t.trim()),
        learning_outcomes: formData.learning_outcomes.filter(o => o.trim())
      };

      if (isEditing) {
        await axios.put(`${API}/courses/${course.id}`, payload, { headers: getAuthHeader() });
        toast.success("Course updated successfully");
      } else {
        await axios.post(`${API}/courses`, payload, { headers: getAuthHeader() });
        toast.success("Course added successfully");
      }
      onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(isEditing ? "Failed to update course" : "Failed to add course");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Course" : "Add New Course"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update the course information below." : "Fill in all the details for the new PECB course."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Basic Info */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Basic Information</h3>
            
            <div className="grid gap-2">
              <Label htmlFor="title">Course Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., ISO 9001 Lead Auditor"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label>Certification Level</Label>
                <Select
                  value={formData.certification_level}
                  onValueChange={(value) => setFormData({ ...formData, certification_level: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CERTIFICATION_LEVELS.map(level => (
                      <SelectItem key={level} value={level}>{level}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed course description..."
                rows={4}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="prerequisites">Prerequisites</Label>
              <Input
                id="prerequisites"
                value={formData.prerequisites || ""}
                onChange={(e) => setFormData({ ...formData, prerequisites: e.target.value })}
                placeholder="e.g., Basic understanding of quality management, or 'None'"
              />
            </div>
          </div>

          {/* Key Topics */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Key Topics</h3>
            {formData.key_topics.map((topic, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={topic}
                  onChange={(e) => handleArrayFieldChange('key_topics', index, e.target.value)}
                  placeholder={`Topic ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem('key_topics', index)}
                  disabled={formData.key_topics.length === 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('key_topics')}>
              <Plus className="h-4 w-4 mr-2" /> Add Topic
            </Button>
          </div>

          {/* Learning Outcomes */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Learning Outcomes</h3>
            {formData.learning_outcomes.map((outcome, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  value={outcome}
                  onChange={(e) => handleArrayFieldChange('learning_outcomes', index, e.target.value)}
                  placeholder={`Outcome ${index + 1}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem('learning_outcomes', index)}
                  disabled={formData.learning_outcomes.length === 1}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => addArrayItem('learning_outcomes')}>
              <Plus className="h-4 w-4 mr-2" /> Add Outcome
            </Button>
          </div>

          {/* Delivery Formats & Pricing */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Delivery Formats & Pricing</h3>
            <div className="space-y-4">
              {formData.delivery_formats.map((format, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="grid gap-2">
                    <Label>Format</Label>
                    <Select
                      value={format.format}
                      onValueChange={(value) => handleDeliveryFormatChange(index, 'format', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="self-study">Self-Study</SelectItem>
                        <SelectItem value="online-live">Online Live</SelectItem>
                        <SelectItem value="e-learning">E-Learning</SelectItem>
                        <SelectItem value="in-person">In-Person</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Price (USD)</Label>
                    <Input
                      type="number"
                      value={format.price}
                      onChange={(e) => handleDeliveryFormatChange(index, 'price', e.target.value)}
                      placeholder="e.g., 1250"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Duration</Label>
                    <Input
                      value={format.duration}
                      onChange={(e) => handleDeliveryFormatChange(index, 'duration', e.target.value)}
                      placeholder="e.g., 5 days"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Media & Links */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Media & Links</h3>
            
            <div className="grid gap-2">
              <Label htmlFor="image_url">Course Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url || ""}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
              {formData.image_url && (
                <img src={formData.image_url} alt="Preview" className="w-32 h-20 object-cover rounded mt-2" />
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="brochure_url">PECB Brochure URL</Label>
              <Input
                id="brochure_url"
                value={formData.brochure_url || ""}
                onChange={(e) => setFormData({ ...formData, brochure_url: e.target.value })}
                placeholder="https://pecb.com/pdf/brochures/..."
              />
            </div>
          </div>

          {/* Settings */}
          <div className="grid gap-4">
            <h3 className="font-semibold text-lg border-b pb-2">Settings</h3>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Featured Course</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Active (visible on site)</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-brand hover:bg-brand-dark">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : (isEditing ? "Update Course" : "Add Course")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CourseEditor;
